const STORAGE_KEY = 'fw_attribution'
let volatileAttribution: AttributionData | null = null

const UTM_KEYS = [
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_content',
  'utm_term',
] as const

const CLICK_ID_KEYS = [
  'gclid',
  'fbclid',
  'msclkid',
  'ttclid',
  'li_fat_id',
  'wbraid',
  'gbraid',
] as const

export type UtmKey = (typeof UTM_KEYS)[number]
export type ClickIdKey = (typeof CLICK_ID_KEYS)[number]

export interface AttributionData {
  landing_page: string
  landing_url: string
  referrer: string
  captured_at: string
  current_page: string
  current_url: string
  query_params: Record<string, string>
  utm_source?: string
  utm_medium?: string
  utm_campaign?: string
  utm_content?: string
  utm_term?: string
  gclid?: string
  fbclid?: string
  msclkid?: string
  ttclid?: string
  li_fat_id?: string
  wbraid?: string
  gbraid?: string
}

function parseQueryParams(search: string): Record<string, string> {
  const params: Record<string, string> = {}
  if (!search) return params
  const searchParams = new URLSearchParams(search.startsWith('?') ? search : `?${search}`)
  searchParams.forEach((value, key) => {
    params[key] = value
  })
  return params
}

function pickKnownParams(
  queryParams: Record<string, string>
): Partial<Pick<AttributionData, UtmKey | ClickIdKey>> {
  const known: Partial<Pick<AttributionData, UtmKey | ClickIdKey>> = {}
  for (const key of [...UTM_KEYS, ...CLICK_ID_KEYS]) {
    const value = queryParams[key]
    if (value) known[key] = value
  }
  return known
}

function mergeFirstTouch(
  existing: Record<string, string | undefined>,
  incoming: Record<string, string>
): Record<string, string | undefined> {
  const merged = { ...existing }
  for (const [key, value] of Object.entries(incoming)) {
    if (!merged[key]) merged[key] = value
  }
  return merged
}

function readStored(): AttributionData | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as AttributionData
  } catch {
    return volatileAttribution
  }
}

function writeStored(data: AttributionData): void {
  volatileAttribution = data
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data))
  } catch {
    // Keep attribution in memory so blocked storage never prevents an enquiry.
  }
}

/** Capture landing + campaign data (first-touch). Safe to call on every route change. */
export function captureAttribution(): AttributionData {
  const queryParams = parseQueryParams(window.location.search)
  const knownParams = pickKnownParams(queryParams)
  const currentPage = `${window.location.pathname}${window.location.search}${window.location.hash}`
  const existing = readStored()

  if (!existing) {
    const data: AttributionData = {
      landing_page: window.location.pathname,
      landing_url: window.location.href,
      referrer: document.referrer || '',
      captured_at: new Date().toISOString(),
      current_page: currentPage,
      current_url: window.location.href,
      query_params: { ...queryParams },
      ...knownParams,
    }
    writeStored(data)
    return data
  }

  const mergedQueryParams = { ...existing.query_params }
  for (const [key, value] of Object.entries(queryParams)) {
    if (!mergedQueryParams[key]) mergedQueryParams[key] = value
  }

  const data: AttributionData = {
    ...existing,
    ...mergeFirstTouch(
      {
        utm_source: existing.utm_source,
        utm_medium: existing.utm_medium,
        utm_campaign: existing.utm_campaign,
        utm_content: existing.utm_content,
        utm_term: existing.utm_term,
        gclid: existing.gclid,
        fbclid: existing.fbclid,
        msclkid: existing.msclkid,
        ttclid: existing.ttclid,
        li_fat_id: existing.li_fat_id,
        wbraid: existing.wbraid,
        gbraid: existing.gbraid,
      },
      knownParams
    ),
    current_page: currentPage,
    current_url: window.location.href,
    query_params: mergedQueryParams,
  }

  writeStored(data)
  return data
}

export function getAttribution(): AttributionData | null {
  if (typeof window === 'undefined') return null
  return readStored() ?? captureAttribution()
}

/** Classify recorded evidence without turning inferred referrals into campaign UTMs. */
export function getAcquisitionSource(data: AttributionData): { source: string; medium: string } {
  if (data.utm_source) return { source: data.utm_source, medium: data.utm_medium ?? 'unknown' }
  if (data.gclid || data.wbraid || data.gbraid) return { source: 'google', medium: 'paid' }
  if (data.msclkid) return { source: 'bing', medium: 'paid' }
  let host: string
  try {
    host = new URL(data.referrer).hostname.toLowerCase()
  } catch {
    return { source: 'direct_or_unknown', medium: 'unknown' }
  }
  const matches = (domain: string) => host === domain || host.endsWith(`.${domain}`)
  for (const [domain, source] of [
    ['chatgpt.com', 'chatgpt'], ['chat.openai.com', 'chatgpt'],
    ['perplexity.ai', 'perplexity'], ['gemini.google.com', 'gemini'],
    ['copilot.microsoft.com', 'copilot'], ['claude.ai', 'claude'],
  ]) {
    if (matches(domain)) return { source, medium: 'ai_referral' }
  }
  if (matches('forteunoweb.com')) return { source: 'internal_or_unknown', medium: 'unknown' }
  return { source: host, medium: 'referral' }
}

/** Flat object for API payloads (backend / CRM). */
export function getAttributionPayload(): Record<string, string | Record<string, string>> {
  const data = getAttribution()
  if (!data) return {}

  return {
    landing_page: data.landing_page,
    landing_url: data.landing_url,
    referrer: data.referrer,
    captured_at: data.captured_at,
    submit_page: data.current_page,
    submit_url: data.current_url,
    utm_source: data.utm_source ?? '',
    utm_medium: data.utm_medium ?? '',
    utm_campaign: data.utm_campaign ?? '',
    utm_content: data.utm_content ?? '',
    utm_term: data.utm_term ?? '',
    gclid: data.gclid ?? '',
    fbclid: data.fbclid ?? '',
    msclkid: data.msclkid ?? '',
    ttclid: data.ttclid ?? '',
    li_fat_id: data.li_fat_id ?? '',
    wbraid: data.wbraid ?? '',
    gbraid: data.gbraid ?? '',
    query_params: data.query_params,
    acquisition_source: getAcquisitionSource(data).source,
    acquisition_medium: getAcquisitionSource(data).medium,
  }
}

/** Calendly inline widget `utm` option (camelCase keys). */
export function getCalendlyUtm(): Record<string, string> {
  const data = getAttribution()
  if (!data) return {}

  const utm: Record<string, string> = {}
  if (data.utm_campaign) utm.utmCampaign = data.utm_campaign
  if (data.utm_source) utm.utmSource = data.utm_source
  if (data.utm_medium) utm.utmMedium = data.utm_medium
  if (data.utm_content) utm.utmContent = data.utm_content
  if (data.utm_term) utm.utmTerm = data.utm_term
  return utm
}

/** Params for gtag events (GA4 custom event parameters). */
export function getGtagAttributionParams(): Record<string, string> {
  const data = getAttribution()
  if (!data) return {}

  const params: Record<string, string> = {
    page_location: data.current_url,
    page_referrer: data.referrer,
    landing_page: data.landing_page,
    acquisition_source: getAcquisitionSource(data).source,
    acquisition_medium: getAcquisitionSource(data).medium,
  }

  for (const key of UTM_KEYS) {
    if (data[key]) params[key] = data[key]
  }
  for (const key of CLICK_ID_KEYS) {
    if (data[key]) params[key] = data[key]
  }

  return params
}

/** Human-readable block for legacy forms (e.g. additional_notes). */
export function formatAttributionNotes(): string {
  const data = getAttribution()
  if (!data) return ''

  const lines: string[] = ['[TRACKING]']
  lines.push(`landing_url: ${data.landing_url}`)
  lines.push(`acquisition_source: ${getAcquisitionSource(data).source}`)
  if (data.referrer) lines.push(`referrer: ${data.referrer}`)
  for (const key of [...UTM_KEYS, ...CLICK_ID_KEYS]) {
    if (data[key]) lines.push(`${key}: ${data[key]}`)
  }
  const known = new Set<string>([...UTM_KEYS, ...CLICK_ID_KEYS])
  for (const [key, value] of Object.entries(data.query_params)) {
    if (!known.has(key)) lines.push(`${key}: ${value}`)
  }
  return `\n${lines.join('\n')}`
}
